import React, { FormEvent, useMemo, useState, useEffect } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  ListGroup,
  Row,
  Spinner,
} from "react-bootstrap";
import AccessDenied from "../components/AccessDenied";
import LoadingPlaceholder from "../components/LoadingPlaceholder";
import { OwnerProfile as OwnerProfileModel } from "../models/owner-profile.model";
import {
  OwnerProfileApiError,
  ownerProfileService,
} from "../services/owner-profile.service";

type AccessState = "checking" | "authorized" | "denied" | "error";
type Operation = "none" | "update" | "promote";

const FEATURE_NAME = "Owner Profile page";
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyProfileForm = { firstName: "", lastName: "", email: "" };

export default function OwnerProfile() {
  const [accessState, setAccessState] = useState<AccessState>("checking");
  const [accessError, setAccessError] = useState("");

  const [accountId, setAccountId] = useState("");
  const [searchError, setSearchError] = useState("");
  const [searching, setSearching] = useState(false);
  const [owner, setOwner] = useState<OwnerProfileModel | null>(null);

  const [profileForm, setProfileForm] = useState(emptyProfileForm);
  const [profileErrors, setProfileErrors] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
  }>({});

  const [staffEmail, setStaffEmail] = useState("");
  const [staffEmailError, setStaffEmailError] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      setAccessState("checking");
      setAccessError("");
      try {
        const hasAccess = await ownerProfileService.checkAccess();
        if (!active) return;
        setAccessState(hasAccess ? "authorized" : "denied");
      } catch (error) {
        if (!active) return;
        const apiError = error as OwnerProfileApiError;
        if (apiError.status === 401 || apiError.status === 403) {
          setAccessState("denied");
        } else {
          setAccessError(
            apiError.message || "Unable to verify access to this page."
          );
          setAccessState("error");
        }
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const programType = owner?.programs?.[0]?.programType ?? "";

  const isProfileDirty = useMemo(() => {
    if (!owner) return false;
    return (
      profileForm.firstName !== owner.ownerFirstName ||
      profileForm.lastName !== owner.ownerLastName ||
      profileForm.email !== owner.ownerEmail
    );
  }, [owner, profileForm]);

  const isPromoteActive = staffEmail.trim().length > 0;

  const activeOperation: Operation = isPromoteActive
    ? "promote"
    : isProfileDirty
    ? "update"
    : "none";

  const profileDisabled = !owner || submitting || isPromoteActive;
  const promoteDisabled = !owner || submitting || isProfileDirty;

  const resetForms = (loaded: OwnerProfileModel | null) => {
    setProfileForm(
      loaded
        ? {
            firstName: loaded.ownerFirstName,
            lastName: loaded.ownerLastName,
            email: loaded.ownerEmail,
          }
        : emptyProfileForm
    );
    setStaffEmail("");
    setProfileErrors({});
    setStaffEmailError("");
    setSubmitError("");
  };

  const handleAuthAwareError = (error: unknown): OwnerProfileApiError => {
    const apiError = error as OwnerProfileApiError;
    if (apiError.status === 401 || apiError.status === 403) {
      setAccessState("denied");
    }
    return apiError;
  };

  const handleSearch = async (event: FormEvent) => {
    event.preventDefault();
    const id = accountId.trim();
    setSearchError("");
    setSuccessMessage("");
    setSubmitError("");

    if (!id) {
      setSearchError("Enter an Owner Account ID to search.");
      return;
    }
    if (searching) return;

    setSearching(true);
    setOwner(null);
    resetForms(null);
    try {
      const result = await ownerProfileService.getOwnerProfile(id);
      setOwner(result);
      resetForms(result);
    } catch (error) {
      setOwner(null);
      const apiError = handleAuthAwareError(error);
      setSearchError(
        apiError.status === 404
          ? `No owner found for Account ID "${id}".`
          : apiError.message || `No owner found for Account ID "${id}".`
      );
    } finally {
      setSearching(false);
    }
  };

  const refreshOwner = async (id: string) => {
    try {
      const result = await ownerProfileService.getOwnerProfile(id);
      setOwner(result);
      resetForms(result);
    } catch {
      // Non-fatal: the mutation already succeeded; keep confirmed values.
    }
  };

  const validateProfile = () => {
    const errors: typeof profileErrors = {};
    if (!profileForm.firstName.trim()) errors.firstName = "First name is required.";
    if (!profileForm.lastName.trim()) errors.lastName = "Last name is required.";
    if (!profileForm.email.trim()) {
      errors.email = "Email is required.";
    } else if (!emailPattern.test(profileForm.email.trim())) {
      errors.email = "Enter a valid email address.";
    }
    setProfileErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStaffEmail = () => {
    if (!emailPattern.test(staffEmail.trim())) {
      setStaffEmailError("Enter a valid staff user email address.");
      return false;
    }
    setStaffEmailError("");
    return true;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!owner || submitting) return;
    setSubmitError("");
    setSuccessMessage("");

    if (isProfileDirty && isPromoteActive) {
      setSubmitError(
        "Choose only one action: update the profile OR promote a staff user."
      );
      return;
    }

    if (activeOperation === "none") {
      setSubmitError(
        "Edit the owner profile, or enter a staff user email to promote."
      );
      return;
    }

    if (!programType) {
      setSubmitError(
        "This owner has no enrolled program, so the operation cannot be submitted."
      );
      return;
    }

    if (activeOperation === "promote") {
      if (!validateStaffEmail()) return;
      setSubmitting(true);
      try {
        await ownerProfileService.promoteOwner({
          ownerAccountId: owner.ownerAccountId,
          staffUserEmail: staffEmail.trim().toLowerCase(),
          programType,
        });
        setSuccessMessage(
          `${staffEmail.trim().toLowerCase()} was promoted to owner successfully.`
        );
        await refreshOwner(owner.ownerAccountId);
      } catch (error) {
        const apiError = handleAuthAwareError(error);
        setSubmitError(
          apiError.message || "Unable to promote the staff user to owner."
        );
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (!validateProfile()) return;
    setSubmitting(true);
    try {
      await ownerProfileService.updateOwnerProfile(owner.ownerAccountId, {
        OwnerFirstName: profileForm.firstName.trim(),
        OwnerLastName: profileForm.lastName.trim(),
        OwnerEmail: profileForm.email.trim().toLowerCase(),
        ProgramType: programType,
      });
      setSuccessMessage("Owner profile updated successfully.");
      await refreshOwner(owner.ownerAccountId);
    } catch (error) {
      const apiError = handleAuthAwareError(error);
      setSubmitError(apiError.message || "Unable to update the owner profile.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    resetForms(owner);
    setSuccessMessage("");
  };

  if (accessState === "checking") return <LoadingPlaceholder />;
  if (accessState === "denied") return <AccessDenied featureName={FEATURE_NAME} />;
  if (accessState === "error") {
    return (
      <Container className="py-5" style={{ maxWidth: 700 }}>
        <Alert variant="danger" className="mb-0">
          {accessError}
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4 pb-5" style={{ maxWidth: 900 }}>
      <div className="mb-4">
        <h1 className="h2 mb-2">Duplicate Email Maintenance</h1>
        <p className="text-muted mb-0">
          Find an owner by Account ID, then update the owner profile or promote a
          staff user to owner. Only one action can be performed at a time.
        </p>
      </div>

      {successMessage && (
        <Alert
          variant="success"
          aria-live="polite"
          dismissible
          onClose={() => setSuccessMessage("")}
        >
          {successMessage}
        </Alert>
      )}

      <Card className="mb-4 shadow-sm">
        <Card.Header as="h2" className="h5 mb-0">
          1. Find Owner
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleSearch} noValidate>
            <Form.Group controlId="ownerAccountId">
              <Form.Label>Owner Account ID</Form.Label>
              <InputGroup hasValidation>
                <Form.Control
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                  isInvalid={Boolean(searchError)}
                  disabled={searching}
                  autoComplete="off"
                  placeholder="e.g. 1234567"
                />
                <Button type="submit" disabled={searching}>
                  {searching ? (
                    <>
                      <Spinner as="span" size="sm" animation="border" role="status" aria-hidden="true" />
                      <span className="ms-2">Searching…</span>
                    </>
                  ) : (
                    "Search"
                  )}
                </Button>
                <Form.Control.Feedback type="invalid">
                  {searchError}
                </Form.Control.Feedback>
              </InputGroup>
            </Form.Group>
          </Form>
        </Card.Body>
      </Card>

      {owner && (
        <Form onSubmit={handleSubmit} noValidate>
          {submitError && (
            <Alert variant="danger" aria-live="assertive">
              {submitError}
            </Alert>
          )}

          {owner.programs.length > 0 && (
            <Card className="mb-4 shadow-sm">
              <Card.Header as="h2" className="h5 mb-0">
                Enrolled Programs
              </Card.Header>
              <ListGroup variant="flush">
                {owner.programs.map((program, index) => (
                  <ListGroup.Item key={program.enrollmentId ?? index}>
                    <span className="fw-semibold">
                      {program.programType || "Program"}
                    </span>
                    {program.loyaltyId ? ` — ${program.loyaltyId}` : ""}
                    {program.enrolled ? " · Enrolled" : ""}
                    {program.comarchEnrolled ? " · Comarch" : ""}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card>
          )}

          <Card className="mb-4 shadow-sm">
            <Card.Header as="h2" className="h5 mb-0">
              2. Update Owner Profile
            </Card.Header>
            <Card.Body>
              {isPromoteActive && (
                <p className="small text-muted">
                  Disabled while a staff user email is entered below. Clear it to
                  edit the profile.
                </p>
              )}
              <Row className="g-3">
                <Col md={4}>
                  <Form.Group controlId="ownerFirstName">
                    <Form.Label>First Name</Form.Label>
                    <Form.Control
                      value={profileForm.firstName}
                      onChange={(e) =>
                        setProfileForm({ ...profileForm, firstName: e.target.value })
                      }
                      isInvalid={Boolean(profileErrors.firstName)}
                      disabled={profileDisabled}
                      autoComplete="off"
                    />
                    <Form.Control.Feedback type="invalid">
                      {profileErrors.firstName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="ownerLastName">
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control
                      value={profileForm.lastName}
                      onChange={(e) =>
                        setProfileForm({ ...profileForm, lastName: e.target.value })
                      }
                      isInvalid={Boolean(profileErrors.lastName)}
                      disabled={profileDisabled}
                      autoComplete="off"
                    />
                    <Form.Control.Feedback type="invalid">
                      {profileErrors.lastName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="ownerEmail">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={profileForm.email}
                      onChange={(e) =>
                        setProfileForm({ ...profileForm, email: e.target.value })
                      }
                      isInvalid={Boolean(profileErrors.email)}
                      disabled={profileDisabled}
                      autoComplete="off"
                    />
                    <Form.Control.Feedback type="invalid">
                      {profileErrors.email}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="mb-4 shadow-sm">
            <Card.Header as="h2" className="h5 mb-0">
              3. Promote Staff User to Owner
            </Card.Header>
            <Card.Body>
              {isProfileDirty && (
                <p className="small text-muted">
                  Disabled while the owner profile is being edited. Cancel your
                  edits to promote a staff user instead.
                </p>
              )}
              <Form.Group controlId="staffUserEmail">
                <Form.Label>Staff User Email</Form.Label>
                <Form.Control
                  type="email"
                  value={staffEmail}
                  onChange={(e) => setStaffEmail(e.target.value)}
                  isInvalid={Boolean(staffEmailError)}
                  disabled={promoteDisabled}
                  autoComplete="off"
                  placeholder="staff.user@example.com"
                />
                <Form.Control.Feedback type="invalid">
                  {staffEmailError}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Promoting a staff user transfers ownership of account #
                  {owner.ownerAccountId}.
                </Form.Text>
              </Form.Group>
            </Card.Body>
          </Card>

          <div className="d-flex justify-content-between">
            <Button
              type="button"
              variant="outline-secondary"
              onClick={handleCancel}
              disabled={submitting || activeOperation === "none"}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || activeOperation === "none"}>
              {submitting ? (
                <>
                  <Spinner as="span" size="sm" animation="border" role="status" aria-hidden="true" />
                  <span className="ms-2">
                    {activeOperation === "promote" ? "Promoting…" : "Updating…"}
                  </span>
                </>
              ) : activeOperation === "promote" ? (
                "Promote Staff User"
              ) : (
                "Update Owner"
              )}
            </Button>
          </div>
        </Form>
      )}
    </Container>
  );
}
